import { adjustHexColor } from "helpers/default";

const baseButton = (color: any = "primary", outline?: boolean, outlineColor?: any, backgroundColor?: any) => {
  const resolvedOutlineColor =
    (baseColors as any)[outlineColor] || outlineColor || (baseColors as any)[color] || color;
  return {
    "& > *": { flexShrink: 0 },
    textDecoration: "none",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    fontSize: "12px",    
    border: '1px solid transparent',
    borderRadius: "6px",
    transition: "all 0.3s",
    paddingX: "15px",
    paddingY: "7px",
    fontWeight: 500,
    lineHeight: "18px",     
    minHeight: "36px",
    maxHeight:"36px",
    minWidth: "30px",   
    maxWidth: "100%", 
    fontFamily : "title",
    ...(outline ? 
      {
        color: color,
        borderColor: outlineColor || color,
        backgroundColor: backgroundColor || "transparent",
        "&:focus": {
          borderColor: color === "danger" ? "#A60000 !important" : `${resolvedOutlineColor} !important`,
          borderStyle: "solid !important",
          borderWidth: "1px !important",
        },
        svg: {
          fill: color,
        }
      } : {
        color: "#ffffff",
        backgroundColor: color,
        "&:focus": { 
          outline: "none",
          borderColor: color === "danger" ? "#A60000 !important" : "transparent !important",
          borderStyle: "solid !important",
          borderWidth: "1px !important",
        },
      }
    ),    
  }
};


const baseBadge = (color = "primary", backgroundColor = "primary", outline?: boolean) => ({
  borderRadius: "6px",
  fontWeight: "500",  
  display: "inline-flex",  
  paddingX: "14px",  
  paddingY: "6px",  
  color: color,
  backgroundColor: backgroundColor,
  border: "1px solid",
  borderColor: outline ? color : 'transparent',
  fontSize: "12px",
  whiteSpace: "nowrap",
  fontFamily:"Poppins"
});


const overflowEllipsis = {
  overflow: "hidden",
  textOverflow: "ellipsis",
};

const baseColors = {
  base_100: "#F7F9FC",
  base_200: "#EDF0F7",
  base_300:"#E2E7F0",
  base_400: "#CBD2E0",
  base_500: "#818181",
  base_600: "#717D96",
  base_700: "#4E4E4E",
  base_800: "#000",
  base_900: "#313131",
  primary: "#000000",
  secondary: "#667E4E",    
  success: "#667E4E",
  warning: "#CAA11C",
  danger: "#A60000",
  info : "#104E9A",        
  green: "#1eb768",
  red: "#EA1A4C",
  blue: "#34AFF7",
  black: "#000",
  orange:"FF7F07",
  white: "#fff",
  text: "#000",
  muted: "#a9b3bf",    
  light_color: "#CBD2E0",
  ltgrey: "#5d5e62",
  mdgrey: "#D6D6D6",
  dkgrey: "#818181",    
  body:"#000",    
  inputOutline: "#EBEBEB",
  inputOutlineDark: "#030303",
  hover: {
    primary: adjustHexColor(40,"#000000"),
    secondary: adjustHexColor(40,"#717D96"),      
    success: adjustHexColor(-40,"#667E4E"),      
    warning: adjustHexColor(-40,"#EED817"),
    danger: adjustHexColor(-40,"#A60000"),
    info: adjustHexColor(-40,"#34AFF7"),
    green: adjustHexColor(-40,"#1eb768"),      
    red: adjustHexColor(-40,"#EA1A4C"),
    blue: adjustHexColor(-40,"#34AFF7"),      
    white: adjustHexColor(-10,"#ffffff"),           
  },
}

// Named breakpoints for styled-components media queries
// Theme-UI responsive array still uses ['991px'] for backward compatibility
export const bp = {
  sm: '576px',   // small phone landscape
  md: '768px',   // tablet portrait
  lg: '991px',   // tablet landscape / small desktop (existing nav collapse)
  xl: '1200px',  // standard desktop
} as const;

export const themevals = {
  breakpoints: ['991px'],
  colors: baseColors,
  fonts: {
    body: "PoppinsReg, sans-serif",
    bold: "Work Sans Semi, sans-serif",
    medium: "Work Sans Medium, sans-serif",
    title: "Rubik, sans-serif",
  },
  fontSizes: [12, 14, 16, 20, 24, 32, 48, 64, 96],
  fontWeights: {
    light: 300,
    body: 400,
    medium: 500,
    semi: 600,
    bold: 700,
    bolder: 900,
  },
  space: [0, 4, 8, 16, 32, 64, 128, 256, 512],
  borders: [0, 6, 12, 24],
  styles: {
    root: {
      fontFamily: "body",
      a: {
        color: "primary",
        fontFamily: "body",
        "&:hover": {
          color: "secondary",
        },
      },
      h1: {
        fontSize: "26px",
        mt: 0,
        mb: 0,
      },
    },
    hr: {
      color: "#BECAD8",
    },
  },
  text: {
    muted: {
      color: "muted",
      fontWeight: 500,
      ...overflowEllipsis,
    },
    primary: {
      color: "#254778",
      fontWeight: 500,
      ...overflowEllipsis,
    },
    secondary: {
      color: "#4893F7",
      fontWeight: 500,
      ...overflowEllipsis,
    },
  },
  forms: {
    input: {
      display: "inline-block",
      borderRadius: 0,
      borderLeft: "none",
      borderRight: "none",
      borderTop: "none",
      padding: "6px 0",
      color: "#254778",
      borderColor: "#BECAD8",
    },
    switch: {
      width: 35,
      bg: "#E9E9EA",
      "input:checked ~ &": {
        bg: "#4893F7",
        div: { transform: "translateX(70%)" },
      },
    },
    checkbox: {
      width: 20,
      height: 20,
    },
    select: {
      fontFamily: "Work Sans",
      color: "primary",
      borderRadius: 0,
      borderColor: "#A9B3BF",
      borderTop: "none",
      borderLeft: "none",
      borderRight: "none",
      padding: "8px 0 4px 0",
      fontSize: 14,
      fontWeight: 500,
      "& + svg": {
        fill: "#A9B3BF",
      },
    },
  },
  buttons: {
    primary: baseButton("primary"),
    secondary: baseButton("secondary"),
    success: baseButton("success"),      
    danger: baseButton("danger"),      
    warning: {
      ...baseButton("warning"),  
      "&:focus": {
        borderColor: "warning",
      }
    },    
    info: baseButton("info"),      
    red: baseButton("red"),      
    green: baseButton("green"),      
    blue: baseButton("blue"),       
    primaryOutline: baseButton("primary", true),
    secondaryOutline: baseButton("primary", true, "mdgrey"),
    dangerSecondary: baseButton("danger", true, "transparent", "#FDF3F3"),
    successOutline: baseButton("success", true),      
    dangerOutline: baseButton("danger", true),      
    warningOutline: baseButton("warning", true),      
    infoOutline: baseButton("info", true),      
    redOutline: baseButton("red", true),      
    greenOutline: baseButton("green", true),      
    blueOutline: baseButton("blue", false),            
    white: {
      ...baseButton("white"),
      color: "body",
      borderColor: "base_300",
      svg: {
        fill: "body",
        path: { fill: "body" },
      },
    }, 
    icon: {
      background: "none",
      color: "body",
      border: "none",
      padding: "0",
      svg: {
        fill: "body",
        path: { fill: "body" },        
      },
      "&:hover": {
        color: "base_600",
        svg: {
          fill: "base_600",
          path: { fill: "base_600" },        
        },
      }
    },         
  },
  badges: {
    primary: baseBadge("primary", adjustHexColor(85,baseColors.primary)),    
    success: baseBadge("success", "#DDE8D2"),          
    danger: baseBadge("danger", adjustHexColor(85,baseColors.danger)),
    info: baseBadge("info", "#DDEDFF"),
    warning: baseBadge("warning", adjustHexColor(85,baseColors.warning)),
    red: baseBadge("red", adjustHexColor(85,baseColors.red)),
    successSolid: baseBadge("white", "success"),
    blue: baseBadge("blue", adjustHexColor(85,baseColors.blue)),
    caution: baseBadge("#CAA11C","#FFFAEA", true),
    orange: baseBadge("#FF7F07", "#FFEFDF", false),  
    grey: baseBadge("white", "dkgrey"),
    ltgrey: baseBadge("dkgrey", "inputOutline"),     
  },
  spinner : {
    info : { color: "info"}
  },
  cards: {
    primary: {
      background: "#ffffff",
      borderRadius: 6,
      boxShadow: '0px 4px 12px 0px rgba(113, 125, 150, 0.05)',
      border: "1px solid",
      borderColor: "base_300",
    },
    compact: {
      borderRadius: 2,
      border: '1px solid',
      borderColor: 'muted',
    },
  },
  links : {
    back : {
      fontSize: 14,
      textDecoration: "none",      
      lineHeight: "20px",
      marginBottom: 30,
      display: "inline-flex",
      gap: "10px",
      color: "dkgrey",
      "svg" : {
        width: "20px",
        fill: "dkgrey",
        path : { fill: "dkgrey" }
      },
      "&:hover" : {
        color: "base_800",
        "svg" : {
          fill: "base_800",
          path : { fill: "base_800" }
        }
      }
    },
    forward : {
      textDecoration:"underline",
      "&:hover" : {
        color: "base_800",
      }
    }
  }  
};
